// A teljes szöveges keresés DOM nélküli magja: az index (search-index.json,
// lásd ../search-index.mjs) beolvasása, a MiniSearch-index felépítése, a
// lekérdezés, az oldalankénti csoportosítás és a snippet/kiemelés
// szövegpozíciói. Szándékosan nem nyúl a DOM-hoz és az app állapotához (a
// verzió szerinti láthatóságot a hívó adja át egy predikátumként), és nincs
// relatív importja: így a Node a tesztekből közvetlenül betölti (a TS-típusokat
// a Node maga távolítja el) — ezért itt csak "törölhető" TS-szintaxis állhat
// (nincs enum, namespace, paraméter-property), és a csak-típus importok
// "import type"-pal mennek. A felületet az app.ts építi.
import MiniSearch from "minisearch";
// Az index formátumverziója — a build (../search-index.mjs
// SEARCH_INDEX_FORMAT) ugyanezt írja bele; eltérésnél a kliens nem keres.
export const SEARCH_INDEX_FORMAT = 1;
// Mezők és súlyok: az oldal címe számít a legtöbbet, utána a szerkesztő
// kulcsszavai, a szakaszcím és a leírás, végül a szöveg. A leírást csak a
// bevezető szakasz kapja (lásd createSearchEngine), különben az oldal minden
// szakaszát egyformán megemelné.
const searchFields = ["title", "keywords", "heading", "description", "text"];
const fieldBoost = { title: 4, keywords: 3, heading: 2, description: 2 };
const minTermLength = 2;
// Előtag-egyezés legalább ennyi karakteres szóra ("számla" → "számlázás"):
// a magyar ragozás miatt minden szóra, nem csak az épp gépelt utolsóra.
const prefixMinLength = 3;
// Egy betűnyi elgépelés ennyi karaktertől.
const fuzzyMinLength = 5;
// A MiniSearch alapértelmezett szóhatára (szóköz/sortörés/írásjel) — a
// kiemelés és a lekérdezés-bontás is ezt használja, hogy ugyanazokat a
// "szavakat" lássa, mint az index.
const wordSeparator = /[\n\r\p{Z}\p{P}]+/u;
const wordPattern = /[^\n\r\p{Z}\p{P}]+/gu;
const combiningMarks = /[̀-ͯ]/g;
// Ékezetmentesítés + kisbetűsítés úgy, hogy a HOSSZ NEM VÁLTOZIK: karakterenként
// a kisbetűs alak NFD-felbontásából a kombináló jeleket elhagyjuk, de csak ha
// ettől a karakter hossza ugyanaz marad. Így a normalizált szövegben talált
// pozíció az eredetiben is ugyanott van (snippet, kiemelés).
export function foldText(value) {
    let result = "";
    for (const character of value) {
        const lower = character.toLowerCase();
        const candidate = lower.length === character.length ? lower : character;
        const folded = candidate.normalize("NFD").replace(combiningMarks, "");
        result += folded.length === candidate.length ? folded : candidate;
    }
    return result;
}
function processTerm(term) {
    const folded = foldText(term);
    return folded.length < minTermLength ? null : folded;
}
// A lekérdezés kereshető (normalizált) szavai — ugyanúgy bontva és
// normalizálva, ahogy a MiniSearch teszi.
export function queryTermsOf(query) {
    return query
        .split(wordSeparator)
        .map(processTerm)
        .filter((term) => term !== null);
}
function isRecord(value) {
    return typeof value === "object" && value !== null && !Array.isArray(value);
}
export function parseSearchIndex(value) {
    if (!isRecord(value) || value.format !== SEARCH_INDEX_FORMAT) {
        throw new Error("A keresési index formátuma ismeretlen.");
    }
    const { files, sections } = value;
    if (!Array.isArray(files) || !files.every((file) => typeof file === "string")) {
        throw new Error("A keresési index fájllistája érvénytelen.");
    }
    if (!Array.isArray(sections)) {
        throw new Error("A keresési index szakaszlistája érvénytelen.");
    }
    const rows = sections.map((row) => {
        if (!Array.isArray(row) || row.length !== 4 || typeof row[0] !== "number" || row[0] < 0 || row[0] >= files.length
            || typeof row[1] !== "string" || typeof row[2] !== "string" || typeof row[3] !== "string") {
            throw new Error("A keresési index egy szakasza érvénytelen.");
        }
        return [row[0], row[1], row[2], row[3]];
    });
    return { format: SEARCH_INDEX_FORMAT, files: files, sections: rows };
}
function extractField(section, field) {
    switch (field) {
        case "id":
            return section.id;
        case "title":
            return section.page.title;
        case "keywords":
            return section.page.keywords.join(" ");
        case "heading":
            return section.heading;
        case "description":
            return section.anchor === "" ? section.page.description ?? "" : "";
        case "text":
            return section.text;
        default:
            return "";
    }
}
function groupByPage(results, sections) {
    const pages = new Map();
    // A MiniSearch pontszám szerint csökkenő sorrendben adja a találatokat, így
    // egy oldal első szakasza egyben a legjobb is.
    for (const result of results) {
        const section = sections[result.id];
        if (!section) {
            continue;
        }
        const hit = { section, score: result.score, terms: result.terms };
        const page = pages.get(section.page.id);
        if (page) {
            page.sections.push(hit);
            // Kis bónusz minden további találó szakaszért: a téma több helyen is
            // előkerül az oldalon, de a legjobb szakasz marad a döntő.
            page.score += hit.score * 0.1;
            for (const term of hit.terms) {
                if (!page.terms.includes(term)) {
                    page.terms.push(term);
                }
            }
        }
        else {
            pages.set(section.page.id, { page: section.page, score: hit.score, sections: [hit], terms: [...hit.terms] });
        }
    }
    return [...pages.values()].sort((left, right) => right.score - left.score);
}
// Felépíti a keresőt. A "pageOfFile" a fájl-utat (pages.yaml revisions[].file,
// ill. changelogs/<verzió>.md) az oldal metaadatára képezi; amit nem ismer
// (pl. egy régebbi pages.yaml-lel nem egyező index), az kimarad. Az
// indexelés darabokban, az eseményhurkot közben elengedve fut (addAllAsync),
// hogy a lap gépelés közben se akadjon meg.
export async function createSearchEngine(index, pageOfFile) {
    const sections = [];
    for (const [fileIndex, anchor, heading, text] of index.sections) {
        const file = index.files[fileIndex];
        const page = file === undefined ? undefined : pageOfFile(file);
        if (file === undefined || !page) {
            continue;
        }
        sections.push({ id: sections.length, file, anchor, heading, text, page });
    }
    const miniSearch = new MiniSearch({
        fields: searchFields,
        storeFields: [],
        extractField,
        processTerm,
    });
    await miniSearch.addAllAsync(sections, { chunkSize: 200 });
    function run(query, combineWith, isVisible) {
        return miniSearch.search(query, {
            combineWith,
            boost: fieldBoost,
            prefix: (term) => term.length >= prefixMinLength,
            fuzzy: (term) => (term.length >= fuzzyMinLength ? 1 : false),
            filter: (result) => {
                const section = sections[result.id];
                return section !== undefined && isVisible(section);
            },
        });
    }
    return {
        sections,
        search(query, isVisible) {
            const queryTerms = queryTermsOf(query);
            if (queryTerms.length === 0) {
                return { queryTerms, pages: [], partial: false };
            }
            let results = run(query, "AND", isVisible);
            let partial = false;
            if (results.length === 0 && queryTerms.length > 1) {
                results = run(query, "OR", isVisible);
                partial = results.length > 0;
            }
            return { queryTerms, pages: groupByPage(results, sections), partial };
        },
    };
}
// A megadott (normalizált) szavak teljes-szó előfordulásai a szövegben,
// [kezdet, vég) párokként, növekvő sorrendben.
export function findTermRanges(text, terms) {
    const wanted = new Set(terms);
    const ranges = [];
    if (wanted.size === 0) {
        return ranges;
    }
    for (const match of foldText(text).matchAll(wordPattern)) {
        if (match.index !== undefined && wanted.has(match[0])) {
            ranges.push([match.index, match.index + match[0].length]);
        }
    }
    return ranges;
}
function segmentsOf(text, ranges, start, end) {
    const segments = [];
    let position = start;
    for (const [rangeStart, rangeEnd] of ranges) {
        if (rangeEnd <= start || rangeStart >= end) {
            continue;
        }
        const from = Math.max(rangeStart, start);
        const to = Math.min(rangeEnd, end);
        if (from > position) {
            segments.push({ text: text.slice(position, from), hit: false });
        }
        segments.push({ text: text.slice(from, to), hit: true });
        position = to;
    }
    if (position < end) {
        segments.push({ text: text.slice(position, end), hit: false });
    }
    return segments;
}
// A teljes szöveg darabokra bontva (találat / nem találat) — a megnyitott
// oldal szövegcsomópontjainak kiemeléséhez.
export function splitByTerms(text, terms) {
    return segmentsOf(text, findTermRanges(text, terms), 0, text.length);
}
// Legfeljebb "maxLength" karakteres részlet az első találat körül (ha nincs
// találat a szövegben — mert pl. csak a címre illeszkedett —, a szöveg
// elejéről), szóhatáron vágva, "…" jelöléssel.
export function buildSnippet(text, terms, maxLength) {
    const ranges = findTermRanges(text, terms);
    let start = 0;
    const first = ranges[0];
    if (first && first[1] > maxLength) {
        start = Math.max(0, first[0] - Math.floor(maxLength / 3));
        const space = text.indexOf(" ", start);
        if (start > 0 && space !== -1 && space < first[0]) {
            start = space + 1;
        }
    }
    let end = Math.min(text.length, start + maxLength);
    if (end < text.length) {
        const space = text.lastIndexOf(" ", end);
        if (space > start + maxLength / 2) {
            end = space;
        }
    }
    const segments = segmentsOf(text, ranges, start, end);
    if (start > 0) {
        segments.unshift({ text: "… ", hit: false });
    }
    if (end < text.length) {
        segments.push({ text: " …", hit: false });
    }
    return segments;
}
